<script setup>
// advup — número animado (count-up 0 -> valor) com easeOutCubic. Usado nos KPIs.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  value: { type: Number, default: 0 },
  duration: { type: Number, default: 900 },
  decimals: { type: Number, default: 0 },
  prefix: { type: String, default: '' },
  suffix: { type: String, default: '' },
});

const display = ref(0);
let raf = null;

const formatted = computed(() =>
  display.value.toLocaleString('pt-BR', {
    minimumFractionDigits: props.decimals,
    maximumFractionDigits: props.decimals,
  })
);

// Quem liga "reduzir movimento" no sistema não quer números correndo na tela.
// Nesse caso o valor aparece pronto — a informação é a mesma, sem a animação.
const semMovimento = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const animate = target => {
  if (raf) cancelAnimationFrame(raf);
  if (semMovimento()) {
    display.value = target;
    return;
  }
  const start = performance.now();
  const from = display.value;
  const step = now => {
    const t = Math.min(1, (now - start) / props.duration);
    const eased = 1 - (1 - t) ** 3; // easeOutCubic
    display.value = from + (target - from) * eased;
    if (t < 1) {
      raf = requestAnimationFrame(step);
    } else {
      display.value = target;
    }
  };
  raf = requestAnimationFrame(step);
};

onMounted(() => animate(props.value));
watch(
  () => props.value,
  v => animate(v)
);
onBeforeUnmount(() => raf && cancelAnimationFrame(raf));
</script>

<template>
  <span>{{ prefix }}{{ formatted }}{{ suffix }}</span>
</template>
